import { P2GBox, P2GChip } from '@parcel2go/core-ui';
import { useTranslation } from '@parcel2go/npm-p2g-app';
import React, { lazy, Suspense, useMemo } from 'react';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { QuoteApiModel } from '~/lib/api/smartsend/types';
import { TranslationNamespaces } from '~/lib/constants';

const LazyCmsContent = lazy(() => import('~/components/content/cms-content'));

const DateRange = (quote: QuoteApiModel): number => {
  const to = quote.Options[0].EstimatedDeliveryDate;
  return to ? Math.round((new Date(to).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;
};

const Covered = (quote: QuoteApiModel): number =>
  quote.Options.filter((o) => o.CoverCost === 0 && o.Covered).map((o) => o.Covered)[0];

const QuoteCardContent = ({
  quote,
  children,
  tags
}: {
  quote: QuoteApiModel;
  children?: React.ReactNode;
  tags?: React.ReactNode;
}) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const covered = useMemo(() => Covered(quote), [quote]);

  const delivery = useMemo(() => {
    const collection = (() => {
      switch (quote.Service.CollectionType) {
        case 'Shop':
          return t('QUOTE_SHOP_COLLECTION_LABEL', { defaultValue: 'Shop Dropoff' });
        case 'Locker':
          return t('QUOTE_LOCKER_COLLECTION_LABEL', { defaultValue: 'Locker Dropoff' });
        case 'Collection':
        case 'Door':
          return t('QUOTE_HOME_COLLECTION_LABEL', { defaultValue: 'Home Collection' });
        default:
          return '';
      }
    })();

    const delivery = (() => {
      switch (quote.Service.DeliveryType) {
        case 'Shop':
          return t('QUOTE_SHOP_DELIVERY_LABEL', { defaultValue: 'Shop Delivery' });
        case 'Door':
          return t('QUOTE_HOME_DELIVERY_LABEL', { defaultValue: 'Home Delivery' });
        case 'Locker':
          return t('QUOTE_LOCKER_DELIVERY_LABEL', { defaultValue: 'Locker Delivery' });
        default:
          return '';
      }
    })();

    return t('QUOTE_DELIVERY_LABEL', {
      defaultValue: '{{collection}} with {{delivery}}',
      collection,
      delivery
    });
  }, [quote.Service.CollectionType, quote.Service.DeliveryType, t]);

  const deliveryWithin = useMemo(() => DateRange(quote), [quote]);

  return (
    <P2GBox display="flex" flexDirection="column" gap={2}>
      <Suspense>
        <LazyCmsContent contentKey={`quote-service-description-${quote.Service.Id}`} />
      </Suspense>
      {children}
      <P2GBox display="flex" flexDirection="row" gap={1} flexWrap="wrap">
        <P2GChip size="small" label={delivery} />
        {quote.Service.Tags.includes('Tracked') && (
          <P2GChip size="small" label={t('QUOTE_TRACKED_LABEL', { defaultValue: 'Tracked' })} />
        )}
        {deliveryWithin > 1 && (
          <P2GChip
            size="small"
            label={t('QUOTE_ESTIMATED_DELIVERY_LABEL', {
              defaultValue: `Delivery within {{days}} days`,
              days: deliveryWithin
            })}
          />
        )}
        {covered && (
          <P2GChip
            size="small"
            label={t('QUOTE_PROTECTION_LABEL', {
              defaultValue: `Includes {{amount}} Protection`,
              amount: toDisplayCurrency(quote.CurrencyCode, covered, 0)
            })}
          />
        )}
        {tags}
      </P2GBox>
    </P2GBox>
  );
};
export default QuoteCardContent;
